import uuid from 'uuid/v4';
import { PriceType } from '@/services/price';
import { DEFAULT_CURRENCY } from '@/constants';
import componentService from '@/services/component';
import materialService from '@/services/material';
import * as priceFragments from '@/services/price/fragments';
import materialUnits from '@/services/material/units';
import service, { IProduct } from '@/services/product';
import { store as DirtyCheckComposer } from '@/composers/dirty-check';
import { diffIdsRemoved, promiseError } from '@/store/utils';
import { IStateProduct, IStateResources } from './types';

const firstMaterialUnit = Object.keys(materialUnits)[0];
const formatProduct = (data: IProduct): IProduct => (
  JSON.parse(
    JSON.stringify({
      care_instructions: '',
      name: '',
      notes: '',
      ...data,

      // provide defaults for database nullable values
      assets: data.assets || [],
      care_symbols: data.care_symbols || {},
      components: data.components || [],
      costs: data.costs || [],
      materials: data.materials || [],
      prices: data.prices || [],
      properties: data.properties || [],
      resource_costs: data.resource_costs || [{
        currency: DEFAULT_CURRENCY,
        resources: {},
      }],
      sizes: data.sizes || [],
    }),
  )
);

export default () => DirtyCheckComposer({
  namespaced: true,
  state: () => ({
    data: {},
    saved: {},
    resources: {},
    view: {
      readOnly: true,
    },
  } as IStateProduct),
  mutations: {
    ADD_NEW_RESOURCE(state: any, {
      type,
      resource,
    }: any) {
      // add to users resources (what appears in the dropdowns)
      const typePlural = `${type}s`;
      state.resources[typePlural] = state.resources[typePlural].concat(resource);

      const additional = type === 'material'
        ? { unit: firstMaterialUnit }
        : {};

      // add to vendor's resources
      const resourceList = state.data[typePlural];
      state.data[typePlural] = resourceList.concat({
        ...additional,
        id: uuid(),
        [`${type}_id`]: resource.id,
        index: resourceList.length,
        qty: 1,
      });
    },
    LOAD(state: IStateProduct, product: IProduct) {
      state.view.readOnly = (this as any).state.auth.user.id !== product.user_id;
      state.data = formatProduct(product);
      state.saved = formatProduct(product);
    },
    LOAD_RESOURCES(
      state: IStateProduct,
      resources: IStateResources,
    ) {
      state.resources = resources;
    },
    REMOVE(state: IStateProduct) {
      // reset all data (prevents isDirty from being true)
      state.data = formatProduct({} as IProduct);
      state.saved = formatProduct({} as IProduct);
    },
    SAVE(state: IStateProduct) {
      state.saved = {
        ...JSON.parse(
          JSON.stringify(state.data),
        ),
      };
    },
  },
  actions: {
    clone: ({ commit, state }: any) => {
      const { saved } = state as {
        saved: IProduct;
      };

      // create id for cloned product
      const cloneId = uuid();
      saved.id = cloneId;
      saved.name = `${saved.name} (Clone)`;
      saved.components = saved.components.map(component => ({
        ...component,
        id: uuid(),
      }));
      saved.materials = saved.materials.map(material => ({
        ...material,
        id: uuid(),
      }));

      return service
        .update(saved.id, {
          payload: saved,
          // these props are props for data that should be removed
          componentIds: [],
          materialIds: [],
          priceRemoveIds: [],

          // add prices
          prices: [
            ...saved.costs.map(cost => ({
              ...cost,
              id: uuid(),
              type: 'product-cost' as PriceType,
              type_id: saved.id,
            })),
            ...saved.prices.map(price => ({
              ...price,
              id: uuid(),
              type: 'product' as PriceType,
              type_id: saved.id,
            })),
          ],
        })
        .then(() => cloneId)
        .catch(promiseError('PRODUCT_CLONE', commit));
    },
    create: ({ commit }: any) => (
      service
        .create({ name: 'Product name' })
        .catch(promiseError('PRODUCT_CREATE', commit))
    ),
    createResource: ({ commit }: any, { type, name }: any) => (
      (type === 'component'
        ? componentService
        : materialService
      ).create({
        name,
      }).then(newResource => {
        commit('ADD_NEW_RESOURCE', {
          type,
          resource: {
            id: newResource.id,
            name,
          },
        } as any);
        return newResource;
      })
    ),
    load: ({ commit, rootState }: any, id: string) => {
      // load resources only if there is a logged in user
      // TODO: only load if logged in user is product owner
      const belongsToUser = rootState.auth.user.id;
      const priceFragment = priceFragments.query();
      let resourceCostFragment = '';
      let resourcePriceFragment = '';
      let costPriceFragment = '';

      if (belongsToUser) {
        // fetch resources for dropdowns
        // TODO: make dropdowns fetch on load so that this won't be needed here
        Promise.all([
          componentService.find(`
            id
            name
            ${priceFragment}
          `),
          materialService.find(`
            id
            name
            color
            composition
            weight
            ${priceFragment}
          `),
        ]).then(result => {
          const [ components, materials ] = result;
          commit('LOAD_RESOURCES', {
            components: components.result,
            materials: materials.result,
          });
        });

        // fetch prices for resources and costs
        resourceCostFragment = `resource_costs`;
        resourcePriceFragment = priceFragment;
        costPriceFragment = priceFragments.query('costs');
      }

      // load product
      return service.get(id, `
        id
        user_id
        care_instructions
        care_symbols
        name
        notes
        properties
        ${resourceCostFragment}
        sizes
        assets (
          order_by: {
            index: asc_nulls_last
          }
        ) {
          id
          src
          index
        }
        components (
          order_by: {
            index: asc
          }
        ) {
          id
          resource_id: component_id
          name
          qty
          index
          detail: component {
            name
            ${resourcePriceFragment}
          }
        }
        materials (
          order_by: {
            index: asc
          }
        ) {
          id
          resource_id: material_id
          name
          qty
          unit
          index
          detail: material {
            name
            color
            composition
            weight
            ${resourcePriceFragment}
          }
        }
        ${priceFragment}
        ${costPriceFragment}
      `)
      .then(result => {
        commit('LOAD', result);
        return result;
      })
      .catch(promiseError('PRODUCT_LOAD', commit));
    },
    remove: ({ commit }: any, id: string) => (
      service
        .remove(id)
        .then(() => commit('REMOVE'))
        .catch(promiseError('PRODUCT_REMOVE', commit))
    ),
    save: ({ commit, state }: any) => {
      const { data, saved } = state as {
        data: IProduct;
        saved: IProduct;
      };
      return service
        .update(data.id, {
          payload: data,

          // determine components and materials to remove
          componentIds: diffIdsRemoved(data.components, saved.components),
          materialIds: diffIdsRemoved(data.materials, saved.materials),

          // add pricing payload
          priceRemoveIds: [
            ...diffIdsRemoved(data.costs, saved.costs),
            ...diffIdsRemoved(data.prices, saved.prices),
          ],
          prices: [
            ...data.costs.map(cost => ({
              ...cost,
              type: 'product-cost' as PriceType,
              type_id: data.id,
            })),
            ...data.prices.map(price => ({
              ...price,
              type: 'product' as PriceType,
              type_id: data.id,
            })),
          ],
        })
        .then(() => commit('SAVE'))
        .catch(promiseError('PRODUCT_SAVE', commit));
    },
    updateProduct: ({ state }: any, data: IProduct) => {
      state.data = data;
    },
  },
});
